import React, { useState, useEffect } from 'react';
import { addDish, updateDish, deleteDish, uploadDishImage, getDishDocId } from '../dishesData';
import './DishEditModal.css';

function DishEditModal({ isOpen, onClose, dish, category, onSave, onDelete }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: null,
    imageFile: null
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (dish) {
      setFormData({
        name: dish.name || '',
        description: dish.description || '',
        image: dish.image || null,
        imageFile: null
      });
    } else {
      setFormData({
        name: '',
        description: '',
        image: null,
        imageFile: null
      });
    }
    setError('');
  }, [dish, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        imageFile: file,
        image: URL.createObjectURL(file)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let imagePath = null;

      if (formData.imageFile) {
        imagePath = await uploadDishImage(
          formData.imageFile,
          category,
          formData.name
        );
      } else if (dish && formData.image && !formData.imageFile) {
        imagePath = dish.imagePath || null;
      }

      const dishData = {
        name: formData.name,
        description: formData.description,
        image: imagePath,
        category: category
      };

      if (dish) {
        const docId = dish.docId || await getDishDocId(dish.name, dish.category);
        if (docId) {
          await updateDish(docId, dish.category, dishData);
        } else {
          throw new Error('Could not find dish to update');
        }
      } else {
        await addDish(dishData);
      }

      onSave();
      onClose();
    } catch (error) {
      setError(error.message || 'Failed to save dish');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async () => {
    if (!window.confirm(`Are you sure you want to delete "${dish.name}"?`)) {
      return;
    }

    setLoading(true);
    try {
      const docId = dish.docId || await getDishDocId(dish.name, dish.category);
      if (docId) {
        await deleteDish(docId, dish.category);
        onDelete();
        onClose();
      } else {
        throw new Error('Could not find dish to delete');
      }
    } catch (error) {
      setError(error.message || 'Failed to delete dish');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content dish-edit-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <h2>{dish ? 'Edit Dish' : 'Add New Dish'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Dish Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="Enter dish name"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows="4"
              placeholder="Enter dish description"
            />
          </div>
          <div className="form-group">
            <label>Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
            {formData.image && (
              <img src={formData.image} alt="Preview" className="image-preview" />
            )}
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="form-actions">
            <button type="submit" disabled={loading} className="submit-button">
              {loading ? 'Saving...' : (dish ? 'Update Dish' : 'Add Dish')}
            </button>
            {dish && (
              <button
                type="button"
                onClick={handleDeleteClick}
                disabled={loading}
                className="delete-button"
              >
                Delete
              </button>
            )}
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DishEditModal;

